import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Loader,
  Center,
  SimpleGrid,
  Avatar,
  Badge,
  Box,
  ThemeIcon,
  ActionIcon,
  Card,
  Divider
} from '@mantine/core';
import { IconPlus, IconUser, IconEdit, IconArrowRight, IconPackage } from '@tabler/icons-react';
import { authAPI, personasAPI, friendsAPI, wishListsAPI, productsAPI, groupsAPI } from '../services/api';
import DashboardStatStrip from '../components/dashboard/DashboardStatStrip';
import AiSpotlightCard from '../components/dashboard/AiSpotlightCard';
import GroupGiftPanel from '../components/dashboard/GroupGiftPanel';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import EmptyDashboardState from '../components/dashboard/EmptyDashboardState';

function DashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [friends, setFriends] = useState([]);
  const [groups, setGroups] = useState([]);
  const [wishLists, setWishLists] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  const loadDashboard = useCallback(async (force = false) => {
    if (loadingData && !force) return;
    if (dataLoaded && !force) return;
    setLoadingData(true);

    try {
      const [, personasRes, friendsRes, wishListsRes, groupsRes] = await Promise.all([
        authAPI.getProfile(),
        personasAPI.getAll(),
        friendsAPI.getFriends(),
        wishListsAPI.getAll(),
        groupsAPI.getAll()
      ]);

      setPersonas(personasRes.data);
      setFriends(friendsRes.data);
      setWishLists(wishListsRes.data);
      setGroups(groupsRes.data || []);

      try {
        const productsRes = await productsAPI.getAll({ limit: 4 });
        setFeaturedProducts(productsRes.data.products || []);
      } catch (err) {
        setFeaturedProducts([]);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
        setIsInitialLoad(false);
        setDataLoaded(true);
      }
      setLoadingData(false);
    }
  }, [isInitialLoad, loadingData, dataLoaded]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (location.pathname === '/') {
      loadDashboard();
    }
  }, [location.pathname, loadDashboard]);

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <Center h="70vh">
        <Stack align="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text c="dimmed" size="lg">Loading your personalized dashboard...</Text>
        </Stack>
      </Center>
    );
  }

  const hasPersonas = personas.length > 0;

  return (
    <Container size="xl" py="xl" px="md">
      <Group justify="space-between" align="flex-start" mb="lg" wrap="wrap" gap="md">
        <Box>
          <Title order={1} size="h2" fw={700} mb={4}>
            {getTimeBasedGreeting()}!
          </Title>
          <Text c="dimmed" size="sm">
            {hasPersonas
              ? "Here's what's happening with your gift planning today"
              : "Let's set up your first persona so the graph engine has something to work with"}
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={16} />}
          variant="gradient"
          gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
          onClick={() => navigate('/personas')}
        >
          New Persona
        </Button>
      </Group>

      <DashboardStatStrip
        stats={{
          personas: personas.length,
          friends: friends.length,
          groups: groups.length,
          wishLists: wishLists.length
        }}
      />

      {!hasPersonas ? (
        <EmptyDashboardState />
      ) : (
        <>
          <Box mb="xl">
            <AiSpotlightCard personas={personas} />
          </Box>

          <Grid gutter="xl">
            <Grid.Col span={{ base: 12, lg: 8 }}>
              <Title order={3} size="h5" mb="sm">Your personas</Title>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                {personas.slice(0, 6).map((persona) => (
                  <Card key={persona.id} p="lg" radius="lg" withBorder shadow="sm">
                    <Group justify="space-between" mb="md">
                      <Text
                        fw={600}
                        size="sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/products?persona=${persona.id}`)}
                      >
                        {persona.name}
                      </Text>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/personas/new?edit=${persona.id}`);
                        }}
                      >
                        <IconEdit size={15} />
                      </ActionIcon>
                    </Group>

                    <Group
                      gap="md"
                      mb="md"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/products?persona=${persona.id}`)}
                    >
                      {persona.profile_picture ? (
                        <Avatar src={persona.profile_picture} size={56} radius="xl" />
                      ) : (
                        <Avatar size={56} radius="xl" variant="gradient" gradient={{ from: 'blue', to: 'cyan', deg: 45 }}>
                          <IconUser size={26} />
                        </Avatar>
                      )}
                      <Stack gap={4} style={{ flex: 1 }}>
                        {persona.relationship && (
                          <Text size="xs" c="dimmed" fw={500}>{persona.relationship}</Text>
                        )}
                        {persona.age && (
                          <Text size="xs" c="dimmed">Age {persona.age}</Text>
                        )}
                      </Stack>
                    </Group>

                    {Array.isArray(persona.hobbies) && persona.hobbies.length > 0 && (
                      <Group gap={4} mb="md">
                        {persona.hobbies.slice(0, 3).map((h) => (
                          <Badge key={h} size="xs" variant="light" color="blue">{h}</Badge>
                        ))}
                      </Group>
                    )}

                    <Divider my="xs" />

                    <Button
                      variant="light"
                      size="xs"
                      fullWidth
                      rightSection={<IconArrowRight size={14} />}
                      onClick={() => navigate(`/products?persona=${persona.id}`)}
                    >
                      Find gifts
                    </Button>
                  </Card>
                ))}
              </SimpleGrid>

              {featuredProducts.length > 0 && (
                <>
                  <Divider my="xl" />
                  <Title order={3} size="h5" mb="sm">Trending gifts</Title>
                  <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                    {featuredProducts.slice(0, 4).map((product) => (
                      <Card key={product.id || product.ebay_item_id} p="md" radius="lg" withBorder shadow="sm">
                        <Center mb="sm">
                          <ThemeIcon size={44} radius="xl" variant="light" color="blue">
                            <IconPackage size={22} />
                          </ThemeIcon>
                        </Center>
                        <Text size="xs" fw={600} lineClamp={2} ta="center" mb={4}>
                          {product.name}
                        </Text>
                        <Text size="sm" fw={700} c="teal" ta="center">
                          ${product.price.toFixed(2)}
                        </Text>
                      </Card>
                    ))}
                  </SimpleGrid>
                </>
              )}
            </Grid.Col>

            <Grid.Col span={{ base: 12, lg: 4 }}>
              <Stack gap="xl">
                <GroupGiftPanel groups={groups} />
                <ActivityFeed personas={personas} friends={friends} groups={groups} />
              </Stack>
            </Grid.Col>
          </Grid>
        </>
      )}
    </Container>
  );
}

export default DashboardPage;
